// src/components/ContactUs.js
import React, { useState } from 'react';
import './ContactUs.css';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    practiceName: '',
    specialty: '',
    noOfProviders: '',
    billingSoftware: '',
    monthlyRevenue: '',
    phone: '',
    email: '',
    challenges: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const specialties = [
    'Cardiology', 'Neurology', 'Pediatrics', 'Dermatology', 'Orthopedics',
    'Ophthalmology', 'Mental Health', 'Nephrology', 'Podiatry', 'Allergy',
    'Rehab', 'Neurosurgery', 'Geriatrics', 'Other'
  ];

  const providerOptions = ['1', '2-5', '6-10', '11-20', '21-50', '50+'];
  const revenueOptions = ['Less than $50k', '$50k - $100k', '$100k - $250k', '$250k - $500k', '$500k - $1M', '$1M+'];

  const cities = [
    'Atlanta, GA', 'Birmingham, AL', 'Nashville, TN', 'Charlotte, NC', 'Raleigh, NC',
    'Columbia, SC', 'Greenville, SC', 'Myrtle Beach, SC', 'Charleston, SC', 'Savannah, GA',
    'Augusta, GA', 'Columbus, OH', 'Dayton, OH', 'Toledo, OH', 'Cleveland, OH',
    'Cincinnati, OH', 'Louisville, KY', 'Lexington, KY', 'Bowling Green, KY', 'Paducah, KY',
    'Knoxville, TN', 'Chattanooga, TN', 'Huntsville, AL', 'Montgomery, AL', 'Tuscaloosa, AL',
    'Mobile, AL'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you! We will contact you shortly.' });
        setFormData({
          fullName: '', practiceName: '', specialty: '', noOfProviders: '',
          billingSoftware: '', monthlyRevenue: '', phone: '', email: '', challenges: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again or call us directly.' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: '', message: '' }), 5000);
    }
  };

  return (
    <main className="contact-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <span className="breadcrumb-item">Home</span>
          <span className="breadcrumb-separator">→</span>
          <span className="breadcrumb-item active">Contact Us</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">Contact Us</h1>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">
            {/* Left Column - Form */}
            <div className="contact-form-section">
              <h2 className="form-section-title">Let's Streamline Your Billing Together</h2>
              <p className="form-section-description">
                Our experts are ready to help your practice improve collections and simplify billing. Reach out today to learn 
                how Providers Care Billing can provide efficient, transparent, and specialty-specific solutions for your clinic. 
                We'll respond quickly to your inquiry and guide you every step of the way.
              </p>
              
              {submitStatus.message && (
                <div className={`form-status ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <input type="text" name="practiceName" placeholder="Practice Name" value={formData.practiceName} onChange={handleChange} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <select name="specialty" value={formData.specialty} onChange={handleChange} required>
                      <option value="">Your Specialty</option>
                      {specialties.map(spec => <option key={spec} value={spec}>{spec}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <select name="noOfProviders" value={formData.noOfProviders} onChange={handleChange} required>
                      <option value="">No Of Providers</option>
                      {providerOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <input type="text" name="billingSoftware" placeholder="EMR / Billing Software Name" value={formData.billingSoftware} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <select name="monthlyRevenue" value={formData.monthlyRevenue} onChange={handleChange} required>
                      <option value="">Monthly Revenue</option>
                      {revenueOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required />
                  </div>
                </div>
                <div className="form-group">
                  <textarea name="challenges" rows="4" placeholder="What challenges are you facing?" value={formData.challenges} onChange={handleChange}></textarea>
                </div>
                <button type="submit" className="submit-contact-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'SUBMIT'}
                </button>
              </form>
            </div>

            {/* Right Column - Contact Info */}
            <div className="contact-info-section">
              <div className="contact-details-card">
                <h3 className="contact-info-title">Contact Information</h3>
                <div className="contact-info-item">
                  <span className="contact-icon">📞</span>
                  <div>
                    <strong>Phone</strong>
                    <p>888-495-3786</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <span className="contact-icon">📠</span>
                  <div>
                    <strong>Fax</strong>
                    <p>908-287-2297</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <span className="contact-icon">✉️</span>
                  <div>
                    <strong>Email</strong>
                    <p>info@providerscarebilling.com</p>
                  </div>
                </div>
                <div className="contact-info-item">
                  <span className="contact-icon">📍</span>
                  <div>
                    <strong>Address</strong>
                    <p>1001 Main Street, Suite 123<br />Downtown, CA 12345</p>
                  </div>
                </div>
              </div>

              {/* Cities Served */}
              <div className="cities-card">
                <h3 className="cities-title">Areas We Serve</h3>
                <div className="cities-grid">
                  {cities.map((city, index) => (
                    <span key={index} className="city-tag">{city}</span>
                  ))}
                </div>
              </div>

              {/* Trust Badges */}
              <div className="trust-badges-contact">
                {/* <div className="badge-item">
                  <div className="badge-rating">4.8</div>
                  <div className="badge-stars">⭐⭐⭐⭐⭐</div>
                </div> */}
                <div className="badge-item">
                  <div className="badge-icon">✓</div>
                  <span>ACCREDITED BUSINESS</span>
                </div>
                <div className="badge-item">
                  <div className="badge-icon">🔒</div>
                  <span>HIPAA COMPLIANT</span>
                </div>
                <div className="badge-item">
                  <div className="badge-icon">⚖️</div>
                  <span>DMCA PROTECTED</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactUs;